// Central SEO configuration for Legal Corridor.
// Single source of truth for canonical URLs, default meta, business (NAP) data,
// social profiles and reusable JSON-LD structured data.

export const SITE_URL = 'https://www.legalcorridors.com';
export const SITE_NAME = 'Legal Corridor';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
export const TWITTER_HANDLE = '@legalcorridor';

export const DEFAULT_TITLE =
    'Legal Corridor | Trusted Law Firm & Advocates in Delhi';

export const DEFAULT_DESCRIPTION =
    'Legal Corridor is a full-service law firm in Delhi offering expert legal advisory, litigation and consultation across civil, criminal, corporate, matrimonial and property law. Speak to experienced advocates today.';

// Business / NAP details used across structured data.
export const BUSINESS = {
    name: SITE_NAME,
    legalName: 'Legal Corridor',
    phone: '+919555690946',
    altPhone: '+917905705906',
    email: 'contact@legalcorridors.com',
    address: {
        street: '40, Hanuman Lane, 2nd Floor, Baba Kharak Singh Marg, Connaught Place',
        locality: 'New Delhi',
        region: 'Delhi',
        postalCode: '110001',
        country: 'IN',
    },
    areaServed: 'India',
    priceRange: '$$',
};

// Verified social profiles (used for schema `sameAs` and OG discovery).
export const SOCIAL_PROFILES = [
    'https://www.facebook.com/share/iXLSc6QPjvKstvvg/?mibextid=LQQJ4d',
    'https://www.instagram.com/amanverma_advocate',
    'https://www.linkedin.com/in/adv-aman-verma-9986b2225',
    'https://www.youtube.com/@Callyouradvocate',
];

// Absolute URL helper — always returns a canonical, trailing-slash-free URL.
export const absoluteUrl = (path = '/') => {
    if (!path || path === '/') return SITE_URL;
    const clean = `/${String(path).replace(/^\/+/, '').replace(/\/+$/, '')}`;
    return `${SITE_URL}${clean}`;
};

// Absolute URL for an asset that may be site-relative (e.g. "/uploads/blog/x.jpg").
// OG/Twitter/JSON-LD images must be absolute.
export const absoluteAsset = (url) => (!url || /^https?:\/\//i.test(url) ? url : absoluteUrl(url));

// Practice areas — used for `knowsAbout` (entity topics) and service pages.
export const PRACTICE_AREAS = [
    'Divorce & Family Law',
    'Child Custody',
    'Property & Real Estate Law',
    'RERA Disputes',
    'Cheque Bounce & Money Recovery',
    'Criminal Defence',
    'Corporate & Commercial Law',
    'Contract Drafting & Review',
    'Civil Litigation',
    'Matrimonial Disputes',
];

// Site-wide LegalService + Organization JSON-LD.
// Pass { aggregateRating: { ratingValue, reviewCount } } to surface ⭐ in results.
export const organizationSchema = ({ aggregateRating } = {}) => ({
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    '@id': `${SITE_URL}/#organization`,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: DEFAULT_OG_IMAGE,
    description: DEFAULT_DESCRIPTION,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    priceRange: BUSINESS.priceRange,
    areaServed: BUSINESS.areaServed,
    founder: { '@type': 'Person', name: 'Advocate Aman Verma' },
    knowsAbout: PRACTICE_AREAS,
    address: {
        '@type': 'PostalAddress',
        streetAddress: BUSINESS.address.street,
        addressLocality: BUSINESS.address.locality,
        addressRegion: BUSINESS.address.region,
        postalCode: BUSINESS.address.postalCode,
        addressCountry: BUSINESS.address.country,
    },
    geo: {
        '@type': 'GeoCoordinates',
        latitude: 28.6344091,
        longitude: 77.2067168,
    },
    hasMap: 'https://maps.google.com/?q=40+Hanuman+Lane+Connaught+Place+New+Delhi+110001',
    openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '10:00',
        closes: '21:00',
    },
    ...(aggregateRating && aggregateRating.reviewCount
        ? { aggregateRating: { '@type': 'AggregateRating', ...aggregateRating } }
        : {}),
    sameAs: SOCIAL_PROFILES,
});

// WebSite schema (helps Google understand the site + enables sitelinks).
export const websiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    publisher: { '@id': `${SITE_URL}/#organization` },
});

// BreadcrumbList schema builder. `items` = [{ name, path }].
export const breadcrumbSchema = (items = []) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: absoluteUrl(item.path || '/'),
    })),
});

// Attorney (Person) schema — E-E-A-T signal for a legal team member.
// person = { name, jobTitle, image, url, sameAs? }
export const attorneySchema = (person) => ({
    '@context': 'https://schema.org',
    '@type': 'Attorney',
    name: person.name,
    jobTitle: person.jobTitle,
    image: person.image,
    url: person.url,
    worksFor: { '@id': `${SITE_URL}/#organization` },
    knowsAbout: PRACTICE_AREAS,
    ...(person.sameAs && person.sameAs.length ? { sameAs: person.sameAs } : {}),
    areaServed: BUSINESS.areaServed,
});

// Service schema for a practice area. service = { name, description, path, serviceType }
export const serviceSchema = (service) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.serviceType || service.name,
    name: service.name,
    description: service.description,
    url: absoluteUrl(service.path),
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'Country', name: 'India' },
    audience: { '@type': 'Audience', audienceType: 'Individuals and businesses seeking legal help' },
});

// FAQPage schema. items = [{ question, answer }]
export const faqSchema = (items = []) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
});
